import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
  Modal,
  Tabs,
  Segmented,
  Switch,
  Tag,
  Progress,
  Select,
  Input,
  InputNumber,
  Button,
  Timeline,
  Alert,
  Card,
  Space,
  Tooltip,
  List,
  Row,
  Col,
  Statistic,
  Descriptions,
  Divider
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  ReloadOutlined,
  FilePdfOutlined,
  SendOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import {
  FaWhatsapp,
  FaLocationCrosshairs,
  FaGlobe,
  FaHeadset,
  FaWandMagicSparkles,
  FaBuilding
} from 'react-icons/fa6';
import type { Plan } from '../types';

export interface LicenseManagerModalProps {
  open: boolean;
  client: any | null;
  onClose: () => void;
  token: string | null;
  API_URL: string;
  userProfile: any;
  plans?: Plan[];
  onClientUpdated?: (updatedClient: any) => void;
  initialTab?: string;
}

interface WorkspaceItem {
  id: string;
  name: string;
}

interface HistoryItem {
  id: string;
  title: string;
  summary: string;
  limitsChange?: Array<{ key: string; from?: any; to?: any }>;
  reason?: string;
  actor: string;
  createdAt: string;
}

interface LogItem {
  id: string;
  action: string;
  description: string;
  createdAt: string;
}

export const LicenseManagerModal: React.FC<LicenseManagerModalProps> = ({
  open,
  client,
  onClose,
  token,
  API_URL,
  userProfile,
  plans = [],
  onClientUpdated,
  initialTab = 'info'
}) => {
  if (!client) return null;

  const software = client.software || 'Synapse';
  const isSynapse = software === 'Synapse';
  const canManage = Boolean(
    userProfile?.isSuperAdmin ||
    userProfile?.role === 'superadmin' ||
    userProfile?.permissions?.manageLicenses
  );

  // Tab key sanitizer: ensures 'info' is initially active, and gracefully maps legacy or sub-keys
  const resolveTabKey = (tab?: string): string => {
    if (!tab || tab === 'info') return 'info';
    if (tab === 'license' || tab === 'licensing') return 'license';
    if (tab === 'billing' || tab === 'invoices' || tab === 'payments') return 'billing';
    if (tab === 'addons' || tab === 'features') return 'addons';
    if (tab === 'quotas' || tab === 'storage' || tab === 'workspaces' || tab === 'plan') return 'quotas';
    if (tab === 'history') return 'history';
    return 'info';
  };

  // Active top-level Tab (defaults strictly to 'info')
  const [activeTabKey, setActiveTabKey] = useState<string>(() => resolveTabKey(initialTab));

  useEffect(() => {
    if (open) {
      setActiveTabKey(resolveTabKey(initialTab));
    }
  }, [open, initialTab, client?._id]);

  // Licensing Sub-Mode: 'paid' (monthly/annual activation) vs 'trial' (demo evaluation extension)
  const [licenseSubMode, setLicenseSubMode] = useState<'paid' | 'trial'>('paid');

  // ==========================================
  // DURATION & EXTENSION STATES
  // ==========================================
  const [selectedDurationPreset, setSelectedDurationPreset] = useState<number | 'custom'>(1);
  const [customMonths, setCustomMonths] = useState<number>(1);
  const [additionalSeats, setAdditionalSeats] = useState<number>(0);
  const [extensionRemark, setExtensionRemark] = useState<string>('');
  const [isActivating, setIsActivating] = useState<boolean>(false);

  // Trial Extension states
  const [trialDaysPreset, setTrialDaysPreset] = useState<number | 'custom'>(7);
  const [customTrialDays, setCustomTrialDays] = useState<number>(7);
  const [trialRemark, setTrialRemark] = useState<string>('');
  const [isExtendingDemo, setIsExtendingDemo] = useState<boolean>(false);

  // ==========================================
  // PLAN & QUOTA STATES
  // ==========================================
  const [assignedPlanCode, setAssignedPlanCode] = useState<string>(client.assignedPlan || '');
  const [isUpdatingPlan, setIsUpdatingPlan] = useState<boolean>(false);

  // Storage Limit
  const [storageGbInput, setStorageGbInput] = useState<number>(10);
  const [storageUsage, setStorageUsage] = useState<{ usedGb: number; limitGb: number } | null>(null);
  const [isSavingStorage, setIsSavingStorage] = useState<boolean>(false);

  // Workspaces limit (Synapse)
  const [maxWorkspacesInput, setMaxWorkspacesInput] = useState<number>(1);
  const [isSavingWorkspaces, setIsSavingWorkspaces] = useState<boolean>(false);

  // ==========================================
  // ADD-ONS STATES
  // ==========================================
  const [clientWorkspaces, setClientWorkspaces] = useState<WorkspaceItem[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [featuresState, setFeaturesState] = useState<Record<string, boolean>>({
    whatsappCrm: false,
    liveTracking: false,
    integrations: false
  });
  const [addonsState, setAddonsState] = useState<Record<string, boolean>>({
    customDomain: false,
    prioritySla: false,
    vipOnboarding: false
  });
  const [initialAddonsSnapshot, setInitialAddonsSnapshot] = useState<string>('');
  const [isSavingAddons, setIsSavingAddons] = useState<boolean>(false);
  const [isLoadingCustomizations, setIsLoadingCustomizations] = useState<boolean>(false);

  // ==========================================
  // HISTORY & LOGS STATES
  // ==========================================
  const [subscriptionHistory, setSubscriptionHistory] = useState<HistoryItem[]>([]);
  const [clientLogs, setClientLogs] = useState<LogItem[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(false);
  const [historySubTab, setHistorySubTab] = useState<'changes' | 'audit'>('changes');

  // Current Client Snapshot
  const [currentClient, setCurrentClient] = useState<any>(client);

  useEffect(() => {
    setCurrentClient(client);
    setAssignedPlanCode(client.assignedPlan || '');
    // If client is demo, default sub-mode to trial; if live, default to paid
    if (client.subscriptionStatus === 'demo') {
      setLicenseSubMode('paid'); // Allow admin to convert to paid or extend demo
    } else {
      setLicenseSubMode('paid');
    }
  }, [client]);

  // Fetch customizations and history
  const fetchCustomizations = async (targetWsId?: string) => {
    if (!client?._id) return;
    setIsLoadingCustomizations(true);
    try {
      const wsParam = targetWsId ? `&workspaceId=${targetWsId}` : '';
      const res = await fetch(`${API_URL}/clients/${client._id}/customizations?software=${software}${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const f = data.features || data.customizations?.features || {};
        const a = data.addons || {};
        const newFeatures = {
          whatsappCrm: Boolean(f.whatsappCrm),
          liveTracking: Boolean(f.liveTracking),
          integrations: Boolean(f.integrations)
        };
        const newAddons = {
          customDomain: Boolean(a.customDomain),
          prioritySla: Boolean(a.prioritySla),
          vipOnboarding: Boolean(a.vipOnboarding)
        };
        setFeaturesState(newFeatures);
        setAddonsState(newAddons);
        setInitialAddonsSnapshot(JSON.stringify({ newFeatures, newAddons }));

        if (data.customLimits?.storageGb != null) {
          setStorageGbInput(Number(data.customLimits.storageGb));
        } else if (client.currentLimits?.storageGb) {
          setStorageGbInput(Number(client.currentLimits.storageGb));
        }
        if (data.storageUsage) {
          setStorageUsage({ usedGb: data.storageUsage.usedGb, limitGb: data.storageUsage.limitGb });
        }
        if (data.maxWorkspaces != null) {
          setMaxWorkspacesInput(Number(data.maxWorkspaces));
        }
        if (Array.isArray(data.workspaces)) {
          setClientWorkspaces(data.workspaces);
        }
        if (data.targetWorkspaceId) {
          setSelectedWorkspaceId(data.targetWorkspaceId);
        }
      }
    } catch (err) {
      console.error('Failed to fetch customizations:', err);
    } finally {
      setIsLoadingCustomizations(false);
    }
  };

  const fetchLogsAndHistory = async () => {
    if (!client?._id) return;
    setIsLoadingLogs(true);
    try {
      const [logsRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/clients/${client._id}/logs?software=${software}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/clients/${client._id}/history?software=${software}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      if (logsRes.ok) {
        const logs = await logsRes.json();
        setClientLogs(Array.isArray(logs) ? logs : []);
      }
      if (historyRes.ok) {
        const hData = await historyRes.json();
        setSubscriptionHistory(Array.isArray(hData.history) ? hData.history : []);
      }
    } catch (err) {
      console.error('Failed to fetch logs and history:', err);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Quotations, Invoices & Payments State
  const [clientPayments, setClientPayments] = useState<any[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState<boolean>(false);
  const [isResendingActivation, setIsResendingActivation] = useState<boolean>(false);
  const [simulatingPaymentId, setSimulatingPaymentId] = useState<string | null>(null);

  const handleSimulateClientPayment = async (paymentId: string) => {
    try {
      setSimulatingPaymentId(paymentId);
      const res = await fetch(`${API_URL}/payments/${paymentId}/simulate-webhook-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchClientPayments();
        // Update the modal's OWN state first (functional form, against the
        // freshest currentClient — not the `client` prop closed over at
        // mount time, which can be stale by now) so the "Pending Password
        // Setup" badge/Resend button appear immediately in this same
        // modal, not only after it's closed and reopened. Propagate that
        // same fresh object upward instead of re-deriving from the stale prop.
        setCurrentClient((prev: any) => {
          const updated = { ...prev, subscriptionStatus: 'live', isAccountActivated: false };
          if (onClientUpdated && updated?._id) onClientUpdated(updated);
          return updated;
        });
        Swal.fire({
          title: '🎉 Payment Verified & Activated!',
          html: `
            <div style="text-align: left; font-size: 0.85rem; line-height: 1.6;">
              <p style="color: #059669; font-weight: 600;">✓ Subscription activated successfully</p>
              <p><strong>Official Tax Invoice:</strong> <a href="${API_URL}${data.invoiceUrl}" target="_blank" style="color: #2563eb; text-decoration: underline;">${data.invoiceNumber}</a></p>
              <div style="margin-top: 10px; padding: 10px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px;">
                <div style="font-weight: 600; font-size: 0.78rem; color: #475569; margin-bottom: 4px;">Client Password Setup / Welcome Link:</div>
                <div style="font-family: monospace; font-size: 0.74rem; word-break: break-all; color: #1e293b;">${data.activationUrl}</div>
              </div>
            </div>
          `,
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'Open Password Setup',
          cancelButtonText: 'View Tax Invoice',
          confirmButtonColor: '#2563eb',
          cancelButtonColor: '#0f172a'
        }).then((result) => {
          if (result.isConfirmed) {
            window.open(data.activationUrl, '_blank');
          } else if (result.dismiss === Swal.DismissReason.cancel) {
            window.open(`${API_URL}${data.invoiceUrl}`, '_blank');
          }
        });
      } else {
        Swal.fire('Error', data.message || 'Simulation failed.', 'error');
      }
    } catch (err: any) {
      Swal.fire('Error', err.message || 'Network error.', 'error');
    } finally {
      setSimulatingPaymentId(null);
    }
  };

  const [isApprovingPaymentId, setIsApprovingPaymentId] = useState<string | null>(null);
  const [isRejectingPaymentId, setIsRejectingPaymentId] = useState<string | null>(null);

  const handleApproveOfflinePayment = async (paymentId: string) => {
    const confirm = await Swal.fire({
      title: 'Approve Offline Payment?',
      text: 'Have you verified that the payment funds have been credited to the Saasbuck bank account? Once approved, the client subscription will be activated immediately and a GST Tax Invoice will be generated.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Verify & Activate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#64748b'
    });

    if (!confirm.isConfirmed) return;

    try {
      setIsApprovingPaymentId(paymentId);
      const res = await fetch(`${API_URL}/payments/${paymentId}/approve-offline-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchClientPayments();
        // Same fix as handleSimulateClientPayment above — update local
        // state from the freshest currentClient, not the stale `client`
        // prop, and propagate that same object upward.
        setCurrentClient((prev: any) => {
          const updated = { ...prev, subscriptionStatus: 'live', isAccountActivated: false };
          if (onClientUpdated && updated?._id) onClientUpdated(updated);
          return updated;
        });
        Swal.fire({
          title: '✓ Payment Approved & Activated!',
          text: 'The offline payment has been verified by the Owner. Subscription activated and GST Tax Invoice generated.',
          icon: 'success',
          confirmButtonColor: '#2563eb'
        });
      } else {
        Swal.fire('Approval Error', data.message || 'Failed to approve payment.', 'error');
      }
    } catch (err: any) {
      Swal.fire('Error', err.message || 'Network error approving payment.', 'error');
    } finally {
      setIsApprovingPaymentId(null);
    }
  };

  const handleRejectOfflinePayment = async (paymentId: string) => {
    const { value: reason } = await Swal.fire({
      title: 'Reject Offline Payment',
      input: 'text',
      inputLabel: 'Reason for Rejection',
      inputPlaceholder: 'e.g. Bank statement does not reflect this UTR reference',
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'Please provide a reason for rejecting this payment.';
      },
      showCancelButton: true,
      confirmButtonText: 'Reject Payment',
      confirmButtonColor: '#dc2626'
    });

    if (!reason) return;

    try {
      setIsRejectingPaymentId(paymentId);
      const res = await fetch(`${API_URL}/payments/${paymentId}/reject-offline-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: reason.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchClientPayments();
        Swal.fire('Rejected', 'The offline payment has been marked as rejected.', 'info');
      } else {
        Swal.fire('Error', data.message || 'Failed to reject payment.', 'error');
      }
    } catch (err: any) {
      Swal.fire('Error', err.message || 'Network error rejecting payment.', 'error');
    } finally {
      setIsRejectingPaymentId(null);
    }
  };

  const fetchClientPayments = async () => {
    if (!client?._id) return;
    setIsLoadingPayments(true);
    try {
      const res = await fetch(`${API_URL}/payments/client/${client._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClientPayments(data.payments || []);
      }
    } catch (err) {
      console.error('Failed to fetch client payments:', err);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const handleResendActivation = async (clientId: string) => {
    try {
      setIsResendingActivation(true);
      const res = await fetch(`${API_URL}/payments/${clientId}/resend-activation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        Swal.fire('Invitation Sent', 'A fresh 24-hour activation link has been emailed to the client.', 'success');
      } else {
        Swal.fire('Error', data.message || 'Failed to resend activation link.', 'error');
      }
    } catch (err: any) {
      Swal.fire('Error', err.message || 'Network error.', 'error');
    } finally {
      setIsResendingActivation(false);
    }
  };

  useEffect(() => {
    if (open && client) {
      fetchCustomizations();
      fetchLogsAndHistory();
      fetchClientPayments();
    }
  }, [open, client?._id]);

  const handleWorkspaceChange = (newWsId: string) => {
    setSelectedWorkspaceId(newWsId);
    fetchCustomizations(newWsId);
  };

  // Check if add-ons have unsaved changes
  const hasUnsavedAddons = useMemo(() => {
    const current = JSON.stringify({ newFeatures: featuresState, newAddons: addonsState });
    return initialAddonsSnapshot !== '' && current !== initialAddonsSnapshot;
  }, [featuresState, addonsState, initialAddonsSnapshot]);

  // ==========================================
  // STATUS & DATES CALCULATION
  // ==========================================
  const statusInfo = useMemo(() => {
    const isLive = currentClient.subscriptionStatus === 'live';
    const isDemo = currentClient.subscriptionStatus === 'demo' || (!currentClient.subscriptionStatus && !currentClient.expiryDate);
    const expiry = currentClient.expiryDate ? new Date(currentClient.expiryDate) : null;
    const demoExpiry = currentClient.demoExpiryDate ? new Date(currentClient.demoExpiryDate) : null;
    const now = new Date();

    const trialDuration = 7 * 24 * 60 * 60 * 1000;
    const isDemoExpired = isDemo && (demoExpiry ? demoExpiry < now : new Date(currentClient.createdAt).getTime() + trialDuration < now.getTime());
    const isLiveExpired = isLive && expiry && expiry < now;
    const isExpired = currentClient.subscriptionStatus === 'expired' || isDemoExpired || isLiveExpired;
    const isCancelled = currentClient.subscriptionStatus === 'cancelled';

    let daysRemaining: number | null = null;
    const targetDate = isLive ? expiry : demoExpiry;
    if (targetDate) {
      const diffMs = targetDate.getTime() - now.getTime();
      daysRemaining = Math.round(diffMs / (1000 * 60 * 60 * 24));
    }

    return {
      isLive,
      isDemo,
      isExpired,
      isCancelled,
      expiry,
      demoExpiry,
      daysRemaining
    };
  }, [currentClient]);

  // Effective duration months
  const effectiveMonths = useMemo(() => {
    if (selectedDurationPreset === 'custom') {
      return customMonths > 0 ? customMonths : 1;
    }
    return Number(selectedDurationPreset);
  }, [selectedDurationPreset, customMonths]);

  // Projected New Expiry Date
  const projectedExpiryDate = useMemo(() => {
    let base = statusInfo.expiry ? new Date(statusInfo.expiry) : new Date();
    if (isNaN(base.getTime()) || base < new Date()) {
      base = new Date();
    }
    const targetMonth = base.getMonth() + effectiveMonths;
    const safeExpiry = new Date(base);
    safeExpiry.setMonth(targetMonth);
    if (safeExpiry.getMonth() !== ((targetMonth) % 12 + 12) % 12) {
      safeExpiry.setDate(0);
    }
    return safeExpiry;
  }, [statusInfo.expiry, effectiveMonths]);

  // Effective trial days
  const effectiveTrialDays = useMemo(() => {
    if (trialDaysPreset === 'custom') {
      return customTrialDays > 0 ? customTrialDays : 7;
    }
    return Number(trialDaysPreset);
  }, [trialDaysPreset, customTrialDays]);

  const projectedTrialExpiryDate = useMemo(() => {
    let base = statusInfo.demoExpiry ? new Date(statusInfo.demoExpiry) : new Date();
    if (isNaN(base.getTime()) || base < new Date()) {
      base = new Date();
    }
    return new Date(base.getTime() + effectiveTrialDays * 24 * 60 * 60 * 1000);
  }, [statusInfo.demoExpiry, effectiveTrialDays]);

  // Pricing breakdown
  const pricingCalculation = useMemo(() => {
    const currentSeats = currentClient.currentSeats ?? currentClient.currentLimits?.maxUsers ?? 1;
    const extraSeats = additionalSeats || 0;
    const totalSeats = currentSeats + extraSeats;
    const baseRate = currentClient.currentPricePerUser ?? (currentClient.planPriceMonthly || (isSynapse ? (effectiveMonths >= 12 ? 499 : 649) : 499));
    const currency = currentClient.planCurrencySymbol || '₹';
    const totalCost = totalSeats * baseRate * effectiveMonths;

    return {
      currentSeats,
      extraSeats,
      totalSeats,
      baseRate,
      currency,
      totalCost
    };
  }, [currentClient, additionalSeats, effectiveMonths, isSynapse]);

  const storagePercent = useMemo(() => {
    if (!storageUsage || !storageUsage.limitGb) return 0;
    return Math.min(100, Math.round((storageUsage.usedGb / storageUsage.limitGb) * 100));
  }, [storageUsage]);

  // ==========================================
  // CLIENT INFO OVERVIEW HELPERS
  // ==========================================
  const registrationDateFormatted = useMemo(() => {
    if (!currentClient.createdAt) return 'Not recorded';
    const d = new Date(currentClient.createdAt);
    return isNaN(d.getTime())
      ? currentClient.createdAt
      : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }, [currentClient.createdAt]);

  const expirationDateFormatted = useMemo(() => {
    const target = statusInfo.expiry || statusInfo.demoExpiry;
    if (!target) return 'Not configured';
    return target.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }, [statusInfo]);

  const upcomingRenewalFormatted = useMemo(() => {
    if (statusInfo.isLive && statusInfo.expiry) {
      return statusInfo.expiry.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    if (statusInfo.isDemo && statusInfo.demoExpiry) {
      return `Trial ends ${statusInfo.demoExpiry.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })}`;
    }
    return 'Renewal Due (Expired)';
  }, [statusInfo]);

  const daysLeftText = useMemo(() => {
    if (statusInfo.daysRemaining == null) return 'Active';
    if (statusInfo.daysRemaining > 0) return `${statusInfo.daysRemaining} days left`;
    if (statusInfo.daysRemaining === 0) return 'Expires today';
    return `Expired ${Math.abs(statusInfo.daysRemaining)} days ago`;
  }, [statusInfo.daysRemaining]);

  const seatsPurchased = useMemo(() => {
    return currentClient.currentSeats ?? currentClient.currentLimits?.maxUsers ?? 1;
  }, [currentClient]);

  const assignedPlanDetails = useMemo(() => {
    const code = currentClient.assignedPlan || assignedPlanCode;
    const matched = plans.find((p) => p.code === code);
    return {
      code,
      name: matched?.name || code || 'Standard Plan',
      price: matched ? `${matched.currencySymbol || '₹'}${matched.priceMonthly}/mo` : '₹499/mo'
    };
  }, [currentClient.assignedPlan, assignedPlanCode, plans]);

  // ==========================================
  // ACTION HANDLERS
  // ==========================================
  const handleActivateOrExtend = async () => {
    if (!canManage) return;

    setIsActivating(true);
    const durationLabel = effectiveMonths === 12 ? '1 Year' : `${effectiveMonths} Month(s)`;
    try {
      const res = await fetch(`${API_URL}/clients/${currentClient._id}/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          software,
          months: effectiveMonths,
          seats: pricingCalculation.extraSeats > 0 ? pricingCalculation.extraSeats : undefined,
          remark: extensionRemark.trim() || undefined
        })
      });
      const data = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok) {
        Swal.fire({
          icon: 'success',
          title: 'License Activated',
          text: `License successfully extended for ${durationLabel}.`,
          timer: 2000,
          showConfirmButton: false
        });
        setCurrentClient(data);
        if (onClientUpdated) onClientUpdated(data);
        fetchLogsAndHistory();
        setAdditionalSeats(0);
        setExtensionRemark('');
      } else {
        Swal.fire('Error', data.message || 'Failed to extend license', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error during license activation', 'error');
    } finally {
      setIsActivating(false);
    }
  };

  const handleExtendTrial = async () => {
    if (!canManage) return;

    setIsExtendingDemo(true);
    try {
      const res = await fetch(`${API_URL}/clients/${currentClient._id}/extend-demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          software,
          days: effectiveTrialDays,
          remark: trialRemark.trim() || undefined
        })
      });
      const data = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Trial Extended',
          text: `Demo trial extended by ${effectiveTrialDays} days.`,
          timer: 2000,
          showConfirmButton: false
        });
        setCurrentClient(data);
        if (onClientUpdated) onClientUpdated(data);
        fetchLogsAndHistory();
        setTrialRemark('');
      } else {
        Swal.fire('Error', data.message || 'Failed to extend trial', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error extending demo', 'error');
    } finally {
      setIsExtendingDemo(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!canManage) return;

    const { value: remark } = await Swal.fire({
      title: `Cancel Subscription?`,
      text: `This will immediately expire ${currentClient.name || currentClient.username}'s license and revoke active CRM access.`,
      input: 'text',
      inputPlaceholder: 'Reason for cancellation (optional)',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Yes, Cancel Subscription',
      cancelButtonText: 'Dismiss'
    });

    if (remark !== undefined) {
      try {
        const res = await fetch(`${API_URL}/clients/${currentClient._id}/cancel-subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ software, remark })
        });
        const data = await res.json().catch(() => ({ message: 'Unexpected server response' }));
        if (res.ok) {
          Swal.fire('Subscription Cancelled', 'Client status updated to expired.', 'success');
          setCurrentClient(data);
          if (onClientUpdated) onClientUpdated(data);
          fetchLogsAndHistory();
        } else {
          Swal.fire('Error', data.message || 'Failed to cancel subscription', 'error');
        }
      } catch (err) {
        Swal.fire('Error', 'Failed to cancel subscription', 'error');
      }
    }
  };

  const handleSaveAddons = async () => {
    if (!canManage) return;
    setIsSavingAddons(true);
    try {
      const res = await fetch(`${API_URL}/clients/${currentClient._id}/customizations?software=${software}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          features: featuresState,
          addons: addonsState,
          targetWorkspaceId: selectedWorkspaceId
        })
      });
      const data = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Add-Ons Saved',
          text: 'Workspace add-ons and feature gates updated successfully.',
          timer: 1800,
          showConfirmButton: false
        });
        setInitialAddonsSnapshot(JSON.stringify({ newFeatures: featuresState, newAddons: addonsState }));
        fetchLogsAndHistory();
        if (onClientUpdated) onClientUpdated(currentClient);
      } else {
        Swal.fire('Error', data.message || 'Failed to update add-ons', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error saving add-ons', 'error');
    } finally {
      setIsSavingAddons(false);
    }
  };

  const handleSaveStorage = async () => {
    if (!canManage) return;
    if (storageGbInput <= 0) {
      Swal.fire('Invalid Value', 'Storage limit must be a positive number in GB.', 'error');
      return;
    }
    setIsSavingStorage(true);
    try {
      const res = await fetch(`${API_URL}/clients/${currentClient._id}/customizations?software=${software}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          customLimits: { storageGb: storageGbInput },
          targetWorkspaceId: selectedWorkspaceId
        })
      });
      const data = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Storage Updated',
          text: `Cloud storage limit updated to ${storageGbInput} GB.`,
          timer: 1800,
          showConfirmButton: false
        });
        fetchCustomizations(selectedWorkspaceId || undefined);
        fetchLogsAndHistory();
      } else {
        Swal.fire('Error', data.message || 'Failed to update storage limit', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error updating storage limit', 'error');
    } finally {
      setIsSavingStorage(false);
    }
  };

  const handleSaveWorkspaces = async () => {
    if (!canManage) return;
    if (maxWorkspacesInput < 1) {
      Swal.fire('Invalid Value', 'Workspaces allowed must be at least 1.', 'error');
      return;
    }
    setIsSavingWorkspaces(true);
    try {
      const res = await fetch(`${API_URL}/clients/${currentClient._id}/customizations?software=${software}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          customLimits: { maxWorkspaces: maxWorkspacesInput },
          targetWorkspaceId: selectedWorkspaceId
        })
      });
      const data = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Workspaces Updated',
          text: `Allowed workspaces updated to ${maxWorkspacesInput}.`,
          timer: 1800,
          showConfirmButton: false
        });
        fetchCustomizations(selectedWorkspaceId || undefined);
        fetchLogsAndHistory();
      } else {
        Swal.fire('Error', data.message || 'Failed to update workspaces allowance', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error saving workspaces allowance', 'error');
    } finally {
      setIsSavingWorkspaces(false);
    }
  };

  const handleApplyPlanChange = async () => {
    if (!canManage) return;
    if (!assignedPlanCode) {
      Swal.fire('Select Plan', 'Please select a plan from the list to assign.', 'warning');
      return;
    }
    setIsUpdatingPlan(true);
    try {
      const res = await fetch(`${API_URL}/clients/${currentClient._id}/customizations?software=${software}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          assignedPlan: assignedPlanCode,
          targetWorkspaceId: selectedWorkspaceId
        })
      });
      const data = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Plan Assigned',
          text: `Plan set to ${assignedPlanCode}.`,
          timer: 1800,
          showConfirmButton: false
        });
        fetchCustomizations(selectedWorkspaceId || undefined);
        fetchLogsAndHistory();
        if (onClientUpdated) {
          onClientUpdated({ ...currentClient, assignedPlan: assignedPlanCode });
        }
      } else {
        Swal.fire('Error', data.message || 'Failed to reassign plan', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error applying plan change', 'error');
    } finally {
      setIsUpdatingPlan(false);
    }
  };

  // Add-on Catalog Definitions
  const ADDONS_CATALOG = [
    {
      key: 'whatsappCrm',
      group: 'features' as const,
      label: 'WhatsApp CRM & Automation',
      description: 'Official Meta Cloud API broadcasting & live team inbox',
      price: '₹1,999/mo',
      scope: 'per workspace',
      enforced: true,
      icon: <FaWhatsapp size={16} color="#16a34a" />,
      iconBg: '#dcfce7'
    },
    {
      key: 'liveTracking',
      group: 'features' as const,
      label: 'Live Employee GPS Tracking',
      description: 'Real-time field staff battery, travel & map routes',
      price: '₹399/employee/mo',
      scope: 'per workspace',
      enforced: true,
      icon: <FaLocationCrosshairs size={16} color="#0891b2" />,
      iconBg: '#cffafe'
    },
    {
      key: 'integrations',
      group: 'features' as const,
      label: 'Integrations Hub & Webhooks',
      description: 'Lead portals, Indiamart, Facebook & outbound webhooks',
      price: '₹999/mo',
      scope: 'per workspace',
      enforced: false,
      icon: <FaWandMagicSparkles size={16} color="#7c3aed" />,
      iconBg: '#ede9fe'
    },
    {
      key: 'customDomain',
      group: 'addons' as const,
      label: 'Custom Domain & White-Label',
      description: 'Custom portal URL, SSL certificate & client branding',
      price: '₹999/mo',
      scope: 'per workspace',
      enforced: false,
      icon: <FaGlobe size={16} color="#2563eb" />,
      iconBg: '#dbeafe'
    },
    {
      key: 'prioritySla',
      group: 'addons' as const,
      label: 'Priority SLA & Dedicated RM',
      description: '15-min emergency response & dedicated account manager',
      price: '₹1,499/mo',
      scope: 'per workspace (10+ users)',
      enforced: false,
      icon: <FaHeadset size={16} color="#d97706" />,
      iconBg: '#fef3c7'
    },
    {
      key: 'vipOnboarding',
      group: 'addons' as const,
      label: 'VIP Migration & Onboarding',
      description: 'Full data migration, team training & custom workflow setup',
      price: '₹2,999 one-time',
      scope: 'per workspace',
      enforced: false,
      icon: <FaBuilding size={16} color="#db2777" />,
      iconBg: '#fce7f3'
    }
  ];

  const availablePlans = plans.filter(p => !p.software || p.software === software);

  // ==========================================
  // TAB ITEMS SPECIFICATION
  // ==========================================
  const tabItems = [
    {
      key: 'info',
      label: <span>Client Info</span>,
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Quick Metrics Row */}
          <Row gutter={12}>
            <Col span={6}>
              <Card size="small" style={{ background: '#f8fafc', borderColor: '#e2e8f0', textAlign: 'center' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Status</div>
                <div
                  style={{
                    fontSize: '0.92rem',
                    fontWeight: 500,
                    color: statusInfo.isExpired ? '#ef4444' : statusInfo.isLive ? '#16a34a' : '#d97706',
                    marginTop: '2px'
                  }}
                >
                  {statusInfo.isExpired ? (statusInfo.isDemo ? 'Trial Expired' : 'Expired') : statusInfo.isLive ? 'Live Active' : 'Demo Trial'}
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" style={{ background: '#f8fafc', borderColor: '#e2e8f0', textAlign: 'center' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Days Remaining</div>
                <div
                  style={{
                    fontSize: '0.92rem',
                    fontWeight: 500,
                    color: statusInfo.daysRemaining != null && statusInfo.daysRemaining < 0 ? '#ef4444' : '#0f172a',
                    marginTop: '2px'
                  }}
                >
                  {daysLeftText}
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" style={{ background: '#f8fafc', borderColor: '#e2e8f0', textAlign: 'center' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Purchased Seats</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 500, color: '#0f172a', marginTop: '2px' }}>
                  {seatsPurchased} {seatsPurchased === 1 ? 'Seat' : 'Seats'}
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" style={{ background: '#f8fafc', borderColor: '#e2e8f0', textAlign: 'center' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Renewal Coming</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 500, color: '#1677ff', marginTop: '2px' }}>
                  {upcomingRenewalFormatted}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Full Client Details in a Clean Descriptions Table */}
          <Card
            size="small"
            title={<span style={{ fontSize: '0.84rem', fontWeight: 500 }}>Account & License Overview</span>}
            extra={
              canManage && (
                <Button
                  size="small"
                  type="link"
                  onClick={() => setActiveTabKey('license')}
                  style={{ padding: 0 }}
                >
                  Manage / Renew License &rarr;
                </Button>
              )
            }
            style={{ borderRadius: '4px', borderColor: '#e2e8f0' }}
          >
            <Descriptions bordered size="small" column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}>
              <Descriptions.Item label="Client Name">
                {currentClient.name || currentClient.username || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Date of Registration">
                {registrationDateFormatted}
              </Descriptions.Item>

              <Descriptions.Item label="Date of Expiration">
                {expirationDateFormatted}
              </Descriptions.Item>
              <Descriptions.Item label="Renewal Date Coming">
                <span style={{ color: '#1677ff', fontWeight: 500 }}>
                  {upcomingRenewalFormatted}
                </span>
              </Descriptions.Item>

              <Descriptions.Item label="Number of Days Left">
                <Tag color={statusInfo.isExpired ? 'error' : statusInfo.daysRemaining && statusInfo.daysRemaining <= 7 ? 'warning' : 'processing'}>
                  {daysLeftText}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Seats Purchased">
                <span style={{ fontWeight: 500 }}>{seatsPurchased}</span> {seatsPurchased === 1 ? 'seat provisioned' : 'seats provisioned'}
              </Descriptions.Item>

              <Descriptions.Item label="Assigned Plan">
                {assignedPlanDetails.name} ({assignedPlanDetails.price})
              </Descriptions.Item>
              <Descriptions.Item label="Software Platform">
                <Tag color={isSynapse ? 'blue' : 'orange'}>{software}</Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Primary Email">
                {currentClient.email || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone Number">
                {currentClient.phone || '—'}
              </Descriptions.Item>

              <Descriptions.Item label="Company / Entity">
                {currentClient.company || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Cloud Storage Quota">
                {storageGbInput} GB
              </Descriptions.Item>

              {isSynapse && (
                <Descriptions.Item label="Allowed Workspaces">
                  {maxWorkspacesInput} workspace(s) ({clientWorkspaces.length} active)
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Client ID" span={isSynapse ? 1 : 2}>
                <code>{currentClient._id}</code>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </div>
      )
    },
    {
      key: 'license',
      label: <span>License & Duration</span>,
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Sub-mode selector if client is eligible for trial extension */}
          {statusInfo.isDemo && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Segmented
                value={licenseSubMode}
                onChange={(val) => setLicenseSubMode(val as 'paid' | 'trial')}
                options={[
                  { label: '💳 Activate / Extend Paid Subscription', value: 'paid' },
                  { label: '⏳ Extend Evaluation Trial', value: 'trial' }
                ]}
                size="middle"
                style={{ background: '#f1f5f9', padding: '3px' }}
              />
            </div>
          )}

          {licenseSubMode === 'paid' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Duration Presets */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.84rem', fontWeight: 500, color: '#334155' }}>
                    Choose Subscription Duration:
                  </span>
                  <Tag color="blue" icon={<ClockCircleOutlined />}>
                    {effectiveMonths === 12 ? '1 Year Annual Term' : `${effectiveMonths} Month Term`}
                  </Tag>
                </div>
                <Segmented
                  value={selectedDurationPreset}
                  onChange={(val) => setSelectedDurationPreset(val as any)}
                  options={[
                    { label: '1 Month', value: 1 },
                    { label: '3 Months', value: 3 },
                    { label: '6 Months', value: 6 },
                    { label: '1 Year (Annual)', value: 12 },
                    { label: 'Custom Months', value: 'custom' }
                  ]}
                  block
                  size="middle"
                  style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '3px' }}
                />

                {selectedDurationPreset === 'custom' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '4px' }}>
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Custom duration:</span>
                    <InputNumber
                      min={1}
                      max={120}
                      value={customMonths}
                      onChange={(val) => setCustomMonths(val || 1)}
                      addonAfter="month(s)"
                      style={{ width: 140 }}
                    />
                  </div>
                )}
              </div>

              {/* 2-Column Organized Grid: Details on Left, Summary & Action on Right */}
              <Row gutter={16}>
                <Col span={14}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Expiry Projection Alert */}
                    <Alert
                      type="info"
                      showIcon
                      icon={<CalendarOutlined />}
                      message={
                        <div style={{ fontSize: '0.8rem' }}>
                          <div>
                            Current Expiry:{' '}
                            <strong>
                              {statusInfo.expiry
                                ? statusInfo.expiry.toLocaleDateString()
                                : statusInfo.demoExpiry
                                ? `${statusInfo.demoExpiry.toLocaleDateString()} (Trial)`
                                : 'Not set'}
                            </strong>
                          </div>
                          <div style={{ marginTop: '2px' }}>
                            New Projected Expiry:{' '}
                            <strong style={{ color: '#1677ff' }}>
                              {projectedExpiryDate.toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </strong>{' '}
                            <Tag color="cyan" style={{ marginLeft: 4 }}>+{effectiveMonths} Mo</Tag>
                          </div>
                        </div>
                      }
                      style={{ borderRadius: '6px' }}
                    />

                    {/* Seats Top-up */}
                    <div>
                      <label style={{ fontSize: '0.78rem', color: '#475569', display: 'block', marginBottom: '4px' }}>
                        Additional Seats (Optional Grant):
                      </label>
                      <InputNumber
                        min={0}
                        max={500}
                        value={additionalSeats}
                        onChange={(val) => setAdditionalSeats(val || 0)}
                        placeholder="0"
                        addonBefore={<UserOutlined />}
                        addonAfter="seats"
                        style={{ width: '100%' }}
                      />
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '3px' }}>
                        Currently provisioned: {pricingCalculation.currentSeats} &rarr; Target: {pricingCalculation.totalSeats} seats
                      </div>
                    </div>

                    {/* Transaction Note */}
                    <div>
                      <label style={{ fontSize: '0.78rem', color: '#475569', display: 'block', marginBottom: '4px' }}>
                        Transaction / Reference Note:
                      </label>
                      <Input
                        placeholder="e.g. Bank transfer ref #10842 or Renewal quote"
                        value={extensionRemark}
                        onChange={(e) => setExtensionRemark(e.target.value)}
                      />
                    </div>
                  </div>
                </Col>

                <Col span={10}>
                  <Card
                    size="small"
                    style={{
                      background: '#f8fafc',
                      borderColor: '#e2e8f0',
                      borderRadius: '6px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '0.74rem', textTransform: 'none', color: '#64748b', fontWeight: 500 }}>
                        License Calculation
                      </div>
                      <Statistic
                        title="Projected Amount"
                        value={pricingCalculation.totalCost}
                        prefix={pricingCalculation.currency}
                        valueStyle={{ color: '#0f172a', fontWeight: 500, fontSize: '1.65rem' }}
                        style={{ margin: '8px 0' }}
                      />
                      <Descriptions column={1} size="small" style={{ fontSize: '0.75rem' }}>
                        <Descriptions.Item label="Rate">
                          {pricingCalculation.currency}{pricingCalculation.baseRate}/mo per user
                        </Descriptions.Item>
                        <Descriptions.Item label="Users">
                          {pricingCalculation.totalSeats} seats
                        </Descriptions.Item>
                        <Descriptions.Item label="Duration">
                          {effectiveMonths} month(s)
                        </Descriptions.Item>
                      </Descriptions>
                    </div>

                    {canManage && (
                      <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Button
                          type="primary"
                          size="large"
                          block
                          icon={<CheckCircleOutlined />}
                          onClick={handleActivateOrExtend}
                          loading={isActivating}
                          style={{ borderRadius: '4px' }}
                        >
                          {statusInfo.isLive
                            ? `Extend License (${effectiveMonths} Mo)`
                            : `Activate License (${effectiveMonths} Mo)`}
                        </Button>

                        {statusInfo.isLive && (
                          <Button
                            danger
                            type="text"
                            size="small"
                            onClick={handleCancelSubscription}
                            style={{ textAlign: 'center' }}
                          >
                            Cancel Subscription
                          </Button>
                        )}
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            </div>
          ) : (
            /* Trial Demo Extension Mode */
            <Card
              size="small"
              title={<span style={{ fontWeight: 500, color: '#92400e' }}>Extend Demo Evaluation Period</span>}
              style={{ borderRadius: '6px', borderColor: '#fde68a', background: '#fffdf5' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', color: '#78350f', display: 'block', marginBottom: '6px' }}>
                    Select Evaluation Extension Days:
                  </label>
                  <Segmented
                    value={trialDaysPreset}
                    onChange={(val) => setTrialDaysPreset(val as any)}
                    options={[
                      { label: '+7 Days', value: 7 },
                      { label: '+14 Days', value: 14 },
                      { label: '+30 Days', value: 30 },
                      { label: 'Custom Days', value: 'custom' }
                    ]}
                    style={{ background: '#fef3c7' }}
                  />

                  {trialDaysPreset === 'custom' && (
                    <InputNumber
                      min={1}
                      max={365}
                      value={customTrialDays}
                      onChange={(val) => setCustomTrialDays(val || 7)}
                      addonAfter="days"
                      style={{ width: 140, marginTop: '8px' }}
                    />
                  )}
                </div>

                <Alert
                  type="warning"
                  showIcon
                  message={
                    <span>
                      Current Demo Expiry:{' '}
                      <strong>{statusInfo.demoExpiry ? statusInfo.demoExpiry.toLocaleDateString() : 'Active'}</strong>
                      {' &rarr; '}
                      New Demo Expiry: <strong>{projectedTrialExpiryDate.toLocaleDateString()}</strong> (+{effectiveTrialDays} days)
                    </span>
                  }
                />

                <Input
                  placeholder="Reason for trial extension (optional)"
                  value={trialRemark}
                  onChange={(e) => setTrialRemark(e.target.value)}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    onClick={handleExtendTrial}
                    loading={isExtendingDemo}
                    style={{
                      background: '#d97706',
                      borderColor: '#d97706',
                      borderRadius: '4px'
                    }}
                  >
                    Extend Trial by {effectiveTrialDays} Days
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )
    },
    ...(isSynapse
      ? [
          {
            key: 'addons',
            label: (
              <span>
                Add-Ons & Modules
                {hasUnsavedAddons && <Tag color="processing" style={{ marginLeft: 6 }}>Unsaved</Tag>}
              </span>
            ),
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Workspace Selector Bar */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: '#f8fafc',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <Space size="middle">
                    <span style={{ fontSize: '0.8rem', color: '#475569', fontWeight: 500 }}>
                      Target Workspace:
                    </span>
                    {clientWorkspaces.length > 1 ? (
                      <Select
                        value={selectedWorkspaceId || clientWorkspaces[0]?.id}
                        onChange={handleWorkspaceChange}
                        disabled={isLoadingCustomizations || isSavingAddons}
                        options={clientWorkspaces.map(ws => ({ label: ws.name, value: ws.id }))}
                        style={{ width: 220 }}
                      />
                    ) : (
                      <Tag color="blue">{clientWorkspaces[0]?.name || "Client's Primary Workspace"}</Tag>
                    )}
                  </Space>
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    Add-ons are applied per workspace
                  </span>
                </div>

                {/* Ant Design List for Add-ons */}
                <List
                  bordered
                  dataSource={ADDONS_CATALOG}
                  renderItem={(addon) => {
                    const isFeature = addon.group === 'features';
                    const checked = isFeature ? featuresState[addon.key] : addonsState[addon.key];

                    return (
                      <List.Item
                        key={addon.key}
                        actions={[
                          <Switch
                            key="switch"
                            checked={Boolean(checked)}
                            disabled={isLoadingCustomizations || isSavingAddons || !canManage}
                            onChange={(newVal) => {
                              if (isFeature) {
                                setFeaturesState((prev) => ({ ...prev, [addon.key]: newVal }));
                              } else {
                                setAddonsState((prev) => ({ ...prev, [addon.key]: newVal }));
                              }
                            }}
                          />
                        ]}
                        style={{
                          padding: '10px 14px',
                          background: '#ffffff',
                          borderLeft: checked ? '3px solid #1677ff' : '3px solid transparent',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <List.Item.Meta
                          avatar={
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 6,
                                background: addon.iconBg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {addon.icon}
                            </div>
                          }
                          title={
                            <Space size="small">
                              <span style={{ fontSize: '0.84rem', fontWeight: 500, color: '#0f172a' }}>
                                {addon.label}
                              </span>
                              {addon.enforced ? (
                                <Tag color="success" style={{ margin: 0, fontSize: '0.68rem' }}>System Enforced</Tag>
                              ) : (
                                <Tooltip title="Recorded for billing/reporting; fulfilled via configuration.">
                                  <Tag color="warning" style={{ margin: 0, fontSize: '0.68rem' }}>Tracked Only</Tag>
                                </Tooltip>
                              )}
                            </Space>
                          }
                          description={
                            <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
                              {addon.description} &bull; <strong style={{ color: '#0f172a' }}>{addon.price}</strong> ({addon.scope})
                            </div>
                          }
                        />
                      </List.Item>
                    );
                  }}
                  style={{ borderRadius: '6px', background: '#fff' }}
                />

                {/* Save Add-Ons Button */}
                {canManage && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                    <Button
                      type={hasUnsavedAddons ? 'primary' : 'default'}
                      onClick={handleSaveAddons}
                      loading={isSavingAddons}
                      disabled={!hasUnsavedAddons || isLoadingCustomizations}
                      style={{ borderRadius: '4px', minWidth: 170 }}
                    >
                      {hasUnsavedAddons ? 'Save Add-On Changes' : 'No Pending Changes'}
                    </Button>
                  </div>
                )}
              </div>
            )
          }
        ]
      : []),
    {
      key: 'billing',
      label: <span>Quotations & Invoicing</span>,
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 600, color: '#0f172a' }}>
                Quotations, Payments & Tax Invoices
              </h4>
              <p style={{ margin: '2px 0 0', fontSize: '0.76rem', color: '#64748b' }}>
                View client quotations, generated Razorpay links, offline transfer details, and India GST Tax Invoices.
              </p>
            </div>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchClientPayments}
              loading={isLoadingPayments}
            >
              Refresh
            </Button>
          </div>

          {isLoadingPayments ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Progress type="circle" percent={70} status="active" size={40} />
              <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#64748b' }}>Loading records...</div>
            </div>
          ) : clientPayments.length === 0 ? (
            <Card size="small" style={{ background: '#f8fafc', borderColor: '#e2e8f0', textAlign: 'center', padding: '24px' }}>
              <p style={{ margin: 0, color: '#64748b', fontSize: '0.84rem' }}>
                No payment or quotation records found for this client.
              </p>
              <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: '0.76rem' }}>
                Records are generated during manual onboarding or online purchases.
              </p>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {clientPayments.map((payment) => (
                <Card
                  key={payment._id}
                  size="small"
                  style={{
                    borderColor: '#e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                  }}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space>
                        <Tag color={
                          payment.status === 'paid' || payment.status === 'completed' || payment.status === 'activated' ? 'green' : 
                          payment.status === 'pending_approval' ? 'orange' :
                          payment.status === 'rejected' ? 'red' :
                          payment.status === 'payment_pending' ? 'gold' : 'blue'
                        }>
                          {payment.status === 'pending_approval' ? 'PENDING OWNER APPROVAL' : payment.status ? payment.status.toUpperCase() : 'PENDING'}
                        </Tag>
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>
                          {payment.plan || 'Subscription'} • {payment.seats || 1} {payment.seats === 1 ? 'Seat' : 'Seats'} ({payment.months || 12} Mo)
                        </span>
                      </Space>
                      <span style={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a' }}>
                        ₹{Number(payment.totalAmount || 0).toLocaleString('en-IN')}
                        <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 400, marginLeft: '4px' }}>(incl. 18% GST)</span>
                      </span>
                    </div>
                  }
                >
                  <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered>
                    <Descriptions.Item label="Payment Mode">
                      {payment.paymentType === 'offline_bank_transfer' ? (
                        <Tag color="purple">Offline Bank Transfer</Tag>
                      ) : (
                        <Tag color="cyan">Razorpay Payment Link</Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Invoice No">
                      {payment.invoiceNumber ? (
                        <span style={{ fontWeight: 600, color: '#2563eb' }}>{payment.invoiceNumber}</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>Pending Payment</span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Date">
                      {payment.paidAt ? new Date(payment.paidAt).toLocaleDateString('en-IN') : new Date(payment.createdAt).toLocaleDateString('en-IN')}
                    </Descriptions.Item>

                    {payment.paymentType === 'offline_bank_transfer' && (
                      <>
                        <Descriptions.Item label="Bank & UTR" span={2}>
                          <div><strong>Bank:</strong> {payment.bankName || 'N/A'}</div>
                          <div><strong>UTR Ref:</strong> <code style={{ color: '#047857' }}>{payment.utrNumber || 'N/A'}</code></div>
                        </Descriptions.Item>
                        {payment.paymentSlipUrl && (
                          <Descriptions.Item label="Slip Proof">
                            <a
                              href={payment.paymentSlipUrl.startsWith('http') ? payment.paymentSlipUrl : `${API_URL.replace('/api', '')}${payment.paymentSlipUrl}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'underline' }}
                            >
                              View Bank Slip
                            </a>
                          </Descriptions.Item>
                        )}
                      </>
                    )}

                    {payment.razorpayPaymentLinkUrl && (
                      <Descriptions.Item label="Razorpay Link" span={3}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <Space>
                            <a href={payment.razorpayPaymentLinkUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: '0.8rem' }}>
                              {payment.razorpayPaymentLinkUrl}
                            </a>
                            <Button
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(payment.razorpayPaymentLinkUrl);
                                Swal.fire({ toast: true, icon: 'success', title: 'Payment link copied!', timer: 1500, showConfirmButton: false, position: 'top-end' });
                              }}
                            >
                              Copy
                            </Button>
                          </Space>
                          {payment.paymentLinkExpiresAt && (
                            <span style={{ fontSize: '0.74rem', color: new Date(payment.paymentLinkExpiresAt) < new Date() ? '#ef4444' : '#b45309' }}>
                              {new Date(payment.paymentLinkExpiresAt) < new Date() ? '⚠️ Link Expired on: ' : '⏱ Link Valid until: '}
                              {new Date(payment.paymentLinkExpiresAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Quotation PDF" span={payment.invoiceNumber ? 1 : 3}>
                      {payment.quotationUrl ? (
                        <a
                          href={payment.quotationUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#b91c1c',
                            fontWeight: 500,
                            fontSize: '0.82rem'
                          }}
                        >
                          <FilePdfOutlined /> View Quotation
                        </a>
                      ) : (
                        <span style={{ color: '#ef4444', fontSize: '0.78rem' }}>No quotation attached</span>
                      )}
                    </Descriptions.Item>

                    {payment.invoiceNumber && (
                      <Descriptions.Item label="Official Tax Invoice" span={2}>
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          icon={<FilePdfOutlined />}
                          onClick={() => window.open(`${API_URL}/payments/invoice/${payment.invoiceNumber}/view`, '_blank')}
                        >
                          View / Print Tax Invoice
                        </Button>
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  {/* Offline Payment Maker-Checker Approval Box */}
                  {payment.status === 'pending_approval' && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px 14px',
                      background: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderRadius: '6px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ClockCircleOutlined style={{ color: '#d97706', fontSize: '1.05rem' }} />
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#92400e' }}>
                              Offline Payment Awaiting Bank Deposit Verification
                            </div>
                            <div style={{ fontSize: '0.74rem', color: '#78350f' }}>
                              Recorded by <strong>{payment.offlineRecordedBy || 'Sales Staff'}</strong> on {new Date(payment.offlineRecordedAt || payment.updatedAt).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        {userProfile?.isSuperAdmin ? (
                          <Space>
                            <Button
                              size="small"
                              danger
                              loading={isRejectingPaymentId === payment._id}
                              onClick={() => handleRejectOfflinePayment(payment._id)}
                            >
                              Reject
                            </Button>
                            <Button
                              size="small"
                              type="primary"
                              loading={isApprovingPaymentId === payment._id}
                              onClick={() => handleApproveOfflinePayment(payment._id)}
                              style={{ background: '#059669', borderColor: '#059669' }}
                            >
                              Verify Deposit & Activate
                            </Button>
                          </Space>
                        ) : (
                          <Tag color="orange">Pending Owner Review</Tag>
                        )}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: '#451a03', background: '#fef3c7', padding: '6px 10px', borderRadius: '4px' }}>
                        <span><strong>Bank:</strong> {payment.bankName || 'N/A'}</span> • 
                        <span style={{ marginLeft: '8px' }}><strong>UTR / Ref:</strong> <code>{payment.utrNumber || 'N/A'}</code></span>
                        {payment.offlineRemark && <span style={{ marginLeft: '8px' }}>• <strong>Remark:</strong> {payment.offlineRemark}</span>}
                      </div>
                    </div>
                  )}

                  {/* Rejected Offline Payment Alert */}
                  {payment.status === 'rejected' && (
                    <div style={{
                      marginTop: '10px',
                      padding: '8px 12px',
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      fontSize: '0.76rem',
                      color: '#991b1b'
                    }}>
                      <strong>Payment Rejected by Owner:</strong> {payment.rejectionReason || 'Bank deposit not verified in Saasbuck account.'}
                      {payment.rejectedBy && ` (Actioned by ${payment.rejectedBy})`}
                    </div>
                  )}

                  {/* Test Simulator: STRICTLY OWNER (Super Admin) ONLY */}
                  {Boolean(userProfile?.isSuperAdmin) && payment.status !== 'completed' && payment.status !== 'paid' && payment.status !== 'activated' && payment.status !== 'pending_approval' && (
                    <div style={{
                      marginTop: '10px',
                      padding: '8px 12px',
                      background: '#f5f3ff',
                      border: '1px dashed #c4b5fd',
                      borderRadius: '6px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <ExperimentOutlined style={{ color: '#7c3aed' }} />
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#5b21b6' }}>
                          Owner Simulator:
                        </span>
                        <span style={{ fontSize: '0.74rem', color: '#6b7280' }}>
                          Simulate Razorpay payment confirmation (Owner Only)
                        </span>
                      </div>
                      <Button
                        size="small"
                        loading={simulatingPaymentId === payment._id}
                        onClick={() => handleSimulateClientPayment(payment._id)}
                        style={{
                          background: '#7c3aed',
                          color: '#ffffff',
                          borderColor: '#7c3aed',
                          fontSize: '0.76rem',
                          fontWeight: 600
                        }}
                      >
                        Simulate Payment
                      </Button>
                    </div>
                  )}

                  <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '8px 12px', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Client Password Setup:</span>
                      {currentClient.isAccountActivated === false ? (
                        <Tag color="warning">Pending Password Setup</Tag>
                      ) : (
                        <Tag color="success">Activated</Tag>
                      )}
                    </div>
                    {currentClient.isAccountActivated === false && (
                      <Button
                        size="small"
                        icon={<SendOutlined />}
                        loading={isResendingActivation}
                        onClick={() => handleResendActivation(currentClient._id)}
                      >
                        Resend Activation Email
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'quotas',
      label: <span>Limits & Quotas</span>,
      children: (
        <Card
          size="small"
          style={{ borderRadius: '6px', borderColor: '#e2e8f0', background: '#ffffff' }}
          styles={{ body: { padding: '16px 18px' } }}
        >
          {/* Section 1: Assigned Plan */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.84rem', fontWeight: 500, color: '#0f172a' }}>
                Assigned Subscription Plan
              </span>
              {currentClient.assignedPlan && (
                <Tag color="geekblue">Current: {currentClient.assignedPlan}</Tag>
              )}
            </div>
            <Row gutter={12} align="middle">
              <Col flex="auto">
                <Select
                  value={assignedPlanCode}
                  onChange={setAssignedPlanCode}
                  disabled={!canManage || isUpdatingPlan}
                  options={[
                    { label: '-- Choose Plan --', value: '' },
                    ...availablePlans.map((p) => ({
                      label: `${p.name} (${p.currencySymbol || '₹'}${p.priceMonthly}/mo)`,
                      value: p.code
                    }))
                  ]}
                  style={{ width: '100%' }}
                />
              </Col>
              {canManage && (
                <Col>
                  <Button
                    type="primary"
                    onClick={handleApplyPlanChange}
                    loading={isUpdatingPlan}
                    disabled={assignedPlanCode === (currentClient.assignedPlan || '')}
                    style={{ borderRadius: '4px' }}
                  >
                    Apply Plan
                  </Button>
                </Col>
              )}
            </Row>
          </div>

          <Divider style={{ margin: '14px 0' }} />

          {/* Section 2: Storage Quota */}
          <div style={{ marginBottom: isSynapse ? '14px' : '0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '0.84rem', fontWeight: 500, color: '#0f172a' }}>
                Cloud Storage Quota
              </span>
              {storageUsage && (
                <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                  {storageUsage.usedGb} GB used of {storageUsage.limitGb} GB limit ({storagePercent}%)
                </span>
              )}
            </div>

            <Progress
              percent={storagePercent}
              status={storagePercent > 90 ? 'exception' : 'normal'}
              strokeColor={storagePercent > 90 ? '#ef4444' : storagePercent > 70 ? '#f59e0b' : '#1677ff'}
              style={{ marginBottom: '10px' }}
            />

            <Row gutter={12} align="middle">
              <Col>
                <InputNumber
                  min={1}
                  max={5000}
                  value={storageGbInput}
                  onChange={(val) => setStorageGbInput(val || 10)}
                  addonAfter="GB"
                  style={{ width: 130 }}
                />
              </Col>
              <Col>
                <Space size="small">
                  {['+5', '+10', '+50'].map((bump) => (
                    <Button
                      key={bump}
                      size="small"
                      onClick={() => setStorageGbInput((prev) => prev + parseInt(bump))}
                    >
                      {bump} GB
                    </Button>
                  ))}
                </Space>
              </Col>
              {canManage && (
                <Col style={{ marginLeft: 'auto' }}>
                  <Button
                    type="primary"
                    onClick={handleSaveStorage}
                    loading={isSavingStorage}
                    style={{ borderRadius: '4px' }}
                  >
                    Save Storage Limit
                  </Button>
                </Col>
              )}
            </Row>
          </div>

          {/* Section 3: Workspaces Quota (Synapse) */}
          {isSynapse && (
            <>
              <Divider style={{ margin: '14px 0' }} />
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.84rem', fontWeight: 500, color: '#0f172a' }}>
                    Account Workspaces Allowance
                  </span>
                  <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                    Currently using {clientWorkspaces.length} workspace(s)
                  </span>
                </div>
                <Row gutter={12} align="middle">
                  <Col>
                    <InputNumber
                      min={1}
                      max={100}
                      value={maxWorkspacesInput}
                      onChange={(val) => setMaxWorkspacesInput(val || 1)}
                      addonAfter="workspaces"
                      style={{ width: 160 }}
                    />
                  </Col>
                  {canManage && (
                    <Col style={{ marginLeft: 'auto' }}>
                      <Button
                        type="primary"
                        onClick={handleSaveWorkspaces}
                        loading={isSavingWorkspaces}
                        style={{ borderRadius: '4px' }}
                      >
                        Save Workspaces Limit
                      </Button>
                    </Col>
                  )}
                </Row>
              </div>
            </>
          )}
        </Card>
      )
    },
    {
      key: 'history',
      label: <span>Audit & History ({subscriptionHistory.length + clientLogs.length})</span>,
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Segmented
              value={historySubTab}
              onChange={(val) => setHistorySubTab(val as any)}
              options={[
                { label: `Subscription History (${subscriptionHistory.length})`, value: 'changes' },
                { label: `Client Activity Logs (${clientLogs.length})`, value: 'audit' }
              ]}
            />
            <Button
              size="small"
              icon={<ReloadOutlined spin={isLoadingLogs} />}
              onClick={fetchLogsAndHistory}
              disabled={isLoadingLogs}
            >
              Refresh
            </Button>
          </div>

          <div style={{ maxHeight: '340px', overflowY: 'auto', padding: '8px 4px' }}>
            {historySubTab === 'changes' ? (
              subscriptionHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '36px', color: '#94a3b8', fontSize: '0.8rem' }}>
                  No plan or subscription changes recorded yet.
                </div>
              ) : (
                <Timeline
                  items={subscriptionHistory.map((entry) => ({
                    color: 'blue',
                    children: (
                      <div style={{ fontSize: '0.78rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <strong style={{ color: '#0f172a' }}>{entry.title}</strong>
                          <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                            {new Date(entry.createdAt).toLocaleDateString()} {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div style={{ color: '#475569', marginTop: '2px' }}>{entry.summary}</div>
                        {entry.limitsChange && entry.limitsChange.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                            {entry.limitsChange.map((c) => (
                              <Tag key={c.key} color="blue" style={{ fontSize: '0.68rem', margin: 0 }}>
                                {c.key}: {c.from ?? '—'} &rarr; {c.to ?? '—'}
                              </Tag>
                            ))}
                          </div>
                        )}
                        <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '3px' }}>
                          by {entry.actor} {entry.reason ? `• Reason: ${entry.reason}` : ''}
                        </div>
                      </div>
                    )
                  }))}
                />
              )
            ) : clientLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '36px', color: '#94a3b8', fontSize: '0.8rem' }}>
                No client activity logs available.
              </div>
            ) : (
              <List
                size="small"
                bordered
                dataSource={clientLogs}
                renderItem={(log) => {
                  const act = log.action.toLowerCase();
                  const isGood = act === 'login' || act === 'subscribe' || act === 'register';
                  const isBad = act === 'unsubscribe' || act === 'logout' || act === 'cancel';
                  return (
                    <List.Item
                      key={log.id}
                      extra={<span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{new Date(log.createdAt).toLocaleString()}</span>}
                    >
                      <Space size="small">
                        <Tag color={isGood ? 'green' : isBad ? 'red' : 'blue'}>
                          {log.action.toUpperCase()}
                        </Tag>
                        <span style={{ fontSize: '0.78rem', color: '#334155' }}>{log.description}</span>
                      </Space>
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
        </div>
      )
    }
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={860}
      centered
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            Client ID: <code style={{ color: '#64748b' }}>{currentClient._id}</code>
          </span>
          <Button onClick={onClose} style={{ borderRadius: '4px' }}>
            Close
          </Button>
        </div>
      }
      title={
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.05rem', fontWeight: 500, color: '#0f172a' }}>
              {currentClient.name || currentClient.username}
            </span>
            <Tag color={isSynapse ? 'blue' : 'orange'} style={{ margin: 0 }}>
              {software}
            </Tag>
            <Tag
              color={statusInfo.isExpired ? 'error' : statusInfo.isLive ? 'success' : 'warning'}
              style={{ margin: 0 }}
            >
              {statusInfo.isExpired
                ? (statusInfo.isDemo ? 'Trial Expired' : 'Expired')
                : statusInfo.isLive
                ? 'Live Subscription'
                : 'Demo Trial'}
            </Tag>
          </div>
          <div style={{ fontSize: '0.76rem', color: '#64748b', fontWeight: 400 }}>
            {currentClient.email || 'No email registered'}
            {currentClient.company ? ` • ${currentClient.company}` : ''}
            {currentClient.phone ? ` • ${currentClient.phone}` : ''}
          </div>
        </div>
      }
      styles={{
        body: {
          padding: '12px 4px 4px 4px',
          maxHeight: '76vh',
          overflowY: 'auto'
        }
      }}
    >
      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={tabItems}
        style={{ marginTop: '4px' }}
      />
    </Modal>
  );
};

export default LicenseManagerModal;
